import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type { ViewPreferences, ViewPlatform, UpdateViewPreferencesData } from '../types';
import { DEFAULT_VIEW_PREFERENCES } from '../types';

/**
 * Narrower response type matching what the API actually returns.
 * The service returns only viewMode/sortBy/sortOrder, not the full entity.
 */
type ViewPreferencesResponse = Pick<ViewPreferences, 'viewMode' | 'sortBy' | 'sortOrder'>;

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch the current user's view preferences for a platform.
 */
async function getViewPreferences(platform: ViewPlatform): Promise<ViewPreferencesResponse> {
  return apiFetch<ViewPreferencesResponse>(`/view-preferences?platform=${platform}`);
}

/**
 * Update the current user's view preferences.
 */
async function updateViewPreferences(
  data: UpdateViewPreferencesData
): Promise<ViewPreferencesResponse> {
  return apiFetch<ViewPreferencesResponse>('/view-preferences', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch view preferences for a platform.
 * Returns DEFAULT_VIEW_PREFERENCES as placeholder data while loading.
 *
 * @param platform - The platform to fetch preferences for ('web' or 'mobile')
 */
export function useViewPreferences(platform: ViewPlatform) {
  return useQuery<ViewPreferencesResponse>({
    queryKey: queryKeys.viewPreferences.byPlatform(platform),
    queryFn: () => getViewPreferences(platform),
    placeholderData: {
      viewMode: DEFAULT_VIEW_PREFERENCES.viewMode,
      sortBy: DEFAULT_VIEW_PREFERENCES.sortBy,
      sortOrder: DEFAULT_VIEW_PREFERENCES.sortOrder,
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to update view preferences.
 * Invalidates the platform-specific query on success.
 */
export function useUpdateViewPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateViewPreferencesData) => updateViewPreferences(data),
    onSuccess: (_, { platform }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.viewPreferences.byPlatform(platform),
      });
    },
  });
}
